import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { devices, integrations, loxone } from '../api/client.js';
import { useApi } from '../api/use-api.js';
import { Badge, Card, PageHeader, SectionTitle, StatusDot } from '../components/ui.js';
import { IconChevronRight } from '../components/icons.js';
import { Data, POPIS_INTEGRACIE, TON_INTEGRACIE, tonDostupnosti } from './common.js';

/*
 * Prehlad.
 *
 * Otazka, na ktoru ma odpovedat na jeden pohlad: "je vsetko v poriadku?"
 * Preto su hore stavy a hned pod nimi zoznam toho, co nefunguje - nie
 * zoznam vsetkeho, co funguje.
 */

function Statistika({
  label,
  value,
  detail,
  tone,
}: {
  readonly label: string;
  readonly value: string | number;
  readonly detail?: string | undefined;
  readonly tone?: 'ok' | 'warn' | 'danger' | 'neutral' | undefined;
}): ReactNode {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span className="text-xs font-medium tracking-wide text-(--color-text-subtle) uppercase">
        {label}
      </span>
      <span className="text-3xl font-semibold tracking-tight text-(--color-text)">{value}</span>
      {detail !== undefined && (
        <span className="inline-flex items-center gap-1.5 text-xs text-(--color-text-muted)">
          {tone !== undefined && <StatusDot tone={tone} />}
          {detail}
        </span>
      )}
    </Card>
  );
}

export function Dashboard(): ReactNode {
  const zariadenia = useApi((signal) => devices.list(signal));
  const integracie = useApi((signal) => integrations.list(signal));
  const miniservery = useApi((signal) => loxone.miniservers(signal));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Prehľad" detail="Stav brány a všetkého, čo cez ňu prechádza." />

      <Data nacitanie={zariadenia}>
        {(data) => {
          const online = data.devices.filter((d) => d.availability.state === 'online');
          const mimo = data.devices.filter((d) => d.availability.state !== 'online');

          return (
            <div className="flex flex-col gap-6">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <Statistika label="Zariadenia" value={data.devices.length} />
                <Statistika
                  label="Dostupné"
                  value={online.length}
                  detail={mimo.length === 0 ? 'všetky odpovedajú' : `${mimo.length} mimo prevádzky`}
                  tone={mimo.length === 0 ? 'ok' : 'warn'}
                />
                <Statistika
                  label="Integrácie"
                  value={integracie.data?.integrations.length ?? 0}
                  detail={
                    integracie.data
                      ? `${integracie.data.integrations.filter((i) => i.state === 'running').length} beží`
                      : undefined
                  }
                  tone="neutral"
                />
                <Statistika
                  label="Miniservery"
                  value={miniservery.data?.miniservers.length ?? 0}
                  detail={
                    miniservery.data
                      ? `${miniservery.data.miniservers.filter((m) => m.connected).length} pripojených`
                      : undefined
                  }
                  tone="neutral"
                />
              </div>

              {mimo.length > 0 && (
                <section className="flex flex-col gap-3">
                  <SectionTitle>Vyžaduje pozornosť</SectionTitle>
                  <ul className="flex flex-col gap-2">
                    {mimo.map((d) => (
                      <Card as="li" key={d.id}>
                        <Link
                          to={`/zariadenia/${d.id}`}
                          className="flex items-center justify-between gap-3 px-4 py-3"
                        >
                          <span className="flex min-w-0 flex-col">
                            <span className="truncate font-medium text-(--color-text)">
                              {d.name}
                            </span>
                            <span className="truncate text-xs text-(--color-text-subtle)">
                              {d.integrationId}
                            </span>
                          </span>
                          <span className="flex shrink-0 items-center gap-2">
                            <Badge tone={tonDostupnosti(d.availability)}>
                              {d.availability.state === 'offline' ? 'Nedostupné' : 'Neznámy stav'}
                            </Badge>
                            <IconChevronRight className="size-4 text-(--color-text-subtle)" />
                          </span>
                        </Link>
                      </Card>
                    ))}
                  </ul>
                </section>
              )}

              {integracie.data && integracie.data.integrations.length > 0 && (
                <section className="flex flex-col gap-3">
                  <SectionTitle>Integrácie</SectionTitle>
                  <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {integracie.data.integrations.map((i) => (
                      <Card as="li" key={i.id} className="flex flex-col gap-1 px-4 py-3">
                        <span className="flex items-center justify-between gap-2">
                          <span className="font-medium text-(--color-text)">{i.name}</span>
                          <Badge tone={TON_INTEGRACIE[i.state]}>{POPIS_INTEGRACIE[i.state]}</Badge>
                        </span>
                        <span className="text-xs text-(--color-text-muted)">{i.detail}</span>
                      </Card>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          );
        }}
      </Data>
    </div>
  );
}
